import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { type ContractService, errors, paxRoute, sessionRequired, staffAuth } from "../../common";
import {
  AccountRiskParams,
  AccountRiskResponse,
  AppAttestChallengeResponse,
  BanAccountBody,
  CardVerificationId,
  CardVerificationOutcome,
  CardVerificationResponse,
  CreateCardVerificationBody,
  CreateSignInCheckBody,
} from "./schemas";

export const service = {
  group: "risk",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Risk API",
  description:
    "The facts an account's standing is scored from: Castle sign-in checks, reCAPTCHA scores and App Attest attestations, and card verifications. Staff read an account's standing and facts and ban or unban it here.",
} as const satisfies ContractService;

const supportAuth = staffAuth("staff.manage");

const noSuchAccount = { NOT_FOUND: { message: "No account has that id." } } as const;

const bannedByModeration = {
  CONFLICT: {
    message: "Moderation banned the account from the whole site; lift that ban from moderation.",
  },
} as const;

export const createSignInCheck = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "session" }),
    openapi({
      method: "POST",
      path: "/api/risk/v1alpha1/sign-in-checks",
      operationId: "risk.signInChecks.create",
      summary: "Check a sign-in with Castle.",
      description:
        "Checks a session that is new or whose last check is 30 days old: sends the token to Castle's Risk API with the user, as a registration when this sign-in created the account and a login otherwise, records Castle's verdict and the invisible bot check that came with it (a reCAPTCHA score on the web, an App Attest attestation on iOS) as facts, and marks the session checked. Clients call it while a new session is pending and whenever a route answers `STEP_UP_REQUIRED` with `sign_in_check`. A verdict never refuses the sign-in or bans; it raises or lowers the account's standing.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "Castle's verdict is recorded, and the session is checked.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    SERVICE_UNAVAILABLE: {
      message: "Castle can't answer right now; the session stays pending. Try again.",
    },
  })
  .input(z.object({ body: CreateSignInCheckBody }));

export const createAppAttestChallenge = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/risk/v1alpha1/app-attest-challenges",
      operationId: "risk.appAttestChallenges.create",
      summary: "Create an App Attest challenge.",
      description:
        "Returns a challenge for attesting an App Attest key, good for this session for five minutes. The iOS app asks for one before each sign-in check, and attests a fresh key against it.",
      successStatus: 201,
      successDescription: "The challenge.",
    }),
  )
  .errors({ ...sessionRequired })
  .output(AppAttestChallengeResponse);

export const createCardVerification = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/risk/v1alpha1/card-verifications",
      operationId: "risk.cardVerifications.create",
      summary: "Create a card verification.",
      description:
        "Starts Stripe Checkout in setup mode for a card, which `verify_person` asks for. Nothing is charged. Send the person to the URL, then complete the verification when they're back; Stripe's webhook completes it too.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The card verification.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    BAD_GATEWAY: { message: "Payments are temporarily unavailable. Try again in a moment." },
  })
  .input(z.object({ body: CreateCardVerificationBody }))
  .output(CardVerificationResponse);

export const completeCardVerification = oc
  .meta(
    paxRoute({ auth: { kind: "session" }, minimumTier: null, cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/risk/v1alpha1/card-verifications/{cardVerificationId}/complete",
      operationId: "risk.cardVerifications.complete",
      summary: "Complete a card verification.",
      description:
        "Called when the person is back from Stripe. Reads the saved card and records it as a `card` fact, unless it's prepaid or already verifies as many accounts as a card can. Safe to repeat. Retry the refused request afterwards.",
      inputStructure: "detailed",
      successDescription: "What the card proved.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    NOT_FOUND: { message: "You have no card verification with that id." },
    BAD_GATEWAY: { message: "Payments are temporarily unavailable. Try again in a moment." },
  })
  .input(z.object({ params: z.object({ cardVerificationId: CardVerificationId }) }))
  .output(CardVerificationOutcome);

export const getAccount = oc
  .meta(
    paxRoute({ auth: supportAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/risk/v1alpha1/accounts/{userId}",
      operationId: "risk.accounts.get",
      summary: "Read an account's risk state.",
      description:
        "Returns whether the account is banned and why, its standing with the breakdown, and its risk facts, newest first.",
      inputStructure: "detailed",
      successDescription: "The account's ban, standing, and facts.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
    ...noSuchAccount,
  })
  .input(z.object({ params: AccountRiskParams }))
  .output(AccountRiskResponse);

export const banAccount = oc
  .meta(
    paxRoute({ auth: supportAuth, minimumTier: "normal", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/risk/v1alpha1/accounts/{userId}/ban",
      operationId: "risk.accounts.ban",
      summary: "Ban an account.",
      description:
        "Suspends the account with the `staff` category and the note, and signs it out everywhere. Every route then answers it with `ACCOUNT_BANNED`.",
      inputStructure: "detailed",
      successDescription: "The account's ban, standing, and facts after the ban.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
    ...noSuchAccount,
    ...bannedByModeration,
  })
  .input(z.object({ params: AccountRiskParams, body: BanAccountBody }))
  .output(AccountRiskResponse);

export const unbanAccount = oc
  .meta(
    paxRoute({ auth: supportAuth, minimumTier: "normal", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/risk/v1alpha1/accounts/{userId}/unban",
      operationId: "risk.accounts.unban",
      summary: "Unban an account.",
      description:
        "Clears the account's ban. Its facts stay, so its standing is what it was before the ban.",
      inputStructure: "detailed",
      successDescription: "The account's ban, standing, and facts after the unban.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
    ...noSuchAccount,
    ...bannedByModeration,
  })
  .input(z.object({ params: AccountRiskParams }))
  .output(AccountRiskResponse);
